import {
  KAActionBucketPageDataLabelValues,
  getKaLabelDisplayName,
  getKaLabelTagColor,
  isCustomLabelValue,
} from '../collections/KAActionBucketPageDataLabels';
import { getAgentCollectionLabels } from './agentCollectionHelpers';

/**
 * A team's own label is its own recommendation type: `requiredFields` holds `custom/<labelId>`
 * next to the enum values. Only registrant agenda makers can use them for now (contract 3).
 */
export const isCustomRequiredField = isCustomLabelValue;

export const PERSONALIZATION_TYPES_WITH_CUSTOM_LABELS = Object.freeze(['registrant']);

export const canUseCustomRecommendationTypes = type =>
  PERSONALIZATION_TYPES_WITH_CUSTOM_LABELS.includes(normalizePersonalizationToolType(type));

/** API wire value is `award`. Older UI drafts used `awardsEntrant`. */
export function normalizePersonalizationToolType(type) {
  if (type === 'awardsEntrant') return 'award';
  return type;
}

export const KA_REQUIRED_FIELDS_OPTIONS_BY_TYPE = Object.freeze({
  registrant: [
    { label: 'Top sessions', value: 'top_sessions' },
    { label: 'Top exhibitors', value: 'top_exhibitors' },
    { label: 'Top passes', value: 'top_passes' },
    { label: 'Top experiences', value: 'top_experiences' },
    { label: 'Top speakers', value: 'top_speakers' },
    { label: 'Event fitment', value: 'event_fitment' },
  ],
  sponsor: [
    { label: 'Sponsorship packages', value: 'sponsorship_packages' },
    { label: 'Matched audience', value: 'matched_audience' },
    { label: 'Event fitment', value: 'event_fitment' },
  ],
  award: [
    { label: 'Award recommendations', value: 'award_recommendations' },
    { label: 'Award judges', value: 'award_judges' },
    { label: 'Award key events', value: 'award_keyevents' },
    { label: 'Event fitment', value: 'event_fitment' },
  ],
});

export const KA_REQUIRED_FIELD_TO_LABELS = Object.freeze({
  top_sessions: [KAActionBucketPageDataLabelValues.PersonalizationTool_Registrant_Sessions],
  top_exhibitors: [KAActionBucketPageDataLabelValues.PersonalizationTool_Registrant_Exhibitors],
  top_passes: [KAActionBucketPageDataLabelValues.PersonalizationTool_Registrant_Passes],
  top_experiences: [KAActionBucketPageDataLabelValues.PersonalizationTool_Registrant_Experiences],
  top_speakers: [KAActionBucketPageDataLabelValues.PersonalizationTool_Registrant_Speakers],
  sponsorship_packages: [
    KAActionBucketPageDataLabelValues.PersonalizationTool_Sponsors_SponsorshipPackages,
  ],
  matched_audience: [
    KAActionBucketPageDataLabelValues.PersonalizationTool_Sponsors_MatchedAudiences,
  ],
  award_recommendations: [
    KAActionBucketPageDataLabelValues.PersonalizationTool_Awards_AwardEntrants,
  ],
  award_judges: [KAActionBucketPageDataLabelValues.PersonalizationTool_Awards_AwardJudges],
  award_keyevents: [
    KAActionBucketPageDataLabelValues.PersonalizationTool_Awards_AwardKeyEvents,
    KAActionBucketPageDataLabelValues.PersonalizationTool_Awards_AwardKeyDates,
  ],
  event_overview: [KAActionBucketPageDataLabelValues.PersonalizationTool_Events_EventInfo],
  event_fitment: [KAActionBucketPageDataLabelValues.PersonalizationTool_Events_EventInfo],
});

const PERSONALIZATION_TYPE_ALIASES = Object.freeze({
  registrants: 'registrant',
  sponsors: 'sponsor',
  awardsEntrant: 'award',
});

export function getKaRequiredFieldLabel(personalizationType, fieldValue) {
  if (isCustomRequiredField(fieldValue)) return getKaLabelDisplayName(fieldValue);
  const normalizedType =
    PERSONALIZATION_TYPE_ALIASES[personalizationType] ||
    normalizePersonalizationToolType(personalizationType);
  const typedOptions = KA_REQUIRED_FIELDS_OPTIONS_BY_TYPE?.[normalizedType] ?? [];
  const typedMatch = typedOptions.find(opt => opt.value === fieldValue);
  if (typedMatch) return typedMatch.label;

  for (const options of Object.values(KA_REQUIRED_FIELDS_OPTIONS_BY_TYPE)) {
    const match = options.find(opt => opt.value === fieldValue);
    if (match) return match.label;
  }

  return fieldValue;
}

export function getKaRequiredFieldTagColor(fieldValue) {
  const kaLabel = getKaRequiredFieldLabels(fieldValue)[0];
  return kaLabel ? getKaLabelTagColor(kaLabel) : 'default';
}

/** The labels a recommendation type draws from: a custom one is its own label. */
export function getKaRequiredFieldLabels(fieldValue) {
  if (isCustomRequiredField(fieldValue)) return [fieldValue];
  return KA_REQUIRED_FIELD_TO_LABELS[fieldValue] ?? [];
}

/**
 * Every recommendation type for a personalisation type: Bridged's, then (for registrants) the
 * team's labels recommended to it. `{ label, value, isCustom, description }`.
 */
export function getKaRecommendationTypeOptions(personalizationType, customLabels = []) {
  const type = normalizePersonalizationToolType(personalizationType);
  const defaults = (KA_REQUIRED_FIELDS_OPTIONS_BY_TYPE?.[type] ?? []).map(option => ({
    ...option,
    isCustom: false,
  }));
  if (!canUseCustomRecommendationTypes(type)) return defaults;
  const yours = (Array.isArray(customLabels) ? customLabels : [])
    .filter(label => isCustomLabelValue(label?.value) && label?.audiences?.includes(type))
    .map(label => ({
      label: label.name,
      value: label.value,
      isCustom: true,
      description: label.description,
    }));
  return [...defaults, ...yours];
}

/**
 * `personalizationToolConfig.sections` for the card (contract 4): one entry per custom
 * `requiredFields` entry whose label still exists, in `requiredFields` order. The description
 * is never sent to the card.
 */
export function buildPersonalizationSections(requiredFields = [], labelsByValue) {
  return (Array.isArray(requiredFields) ? requiredFields : [])
    .filter(isCustomRequiredField)
    .map(key => {
      const label = labelsByValue?.get?.(key);
      if (!label) return null;
      return {
        key,
        label: label.name,
        layout: label.layout,
        details: Array.isArray(label.details) ? label.details : [],
      };
    })
    .filter(Boolean);
}

/** Required fields that no longer have a matching label in the given collections. */
export function getOrphanedRequiredFields(collections, selectedFields = []) {
  const labels = new Set(
    (collections ?? []).flatMap(c => getAgentCollectionLabels(c)).filter(Boolean)
  );

  return selectedFields.filter(field => {
    const needed = getKaRequiredFieldLabels(field);
    return needed?.length > 0 && !needed.some(label => labels.has(label));
  });
}
